import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Word Pyramids — composant jeu principal (anagram / verbal fluency).
 *
 * Pattern Sprint 2 unifié. Level piloté par game-progression (localStorage),
 * promotion bestLevel sur staircase converged. Lang FR par défaut, dérivée
 * de i18next à terme.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActionBarProvider, ActionBarSlot, GameShell, PhaseRenderer, useActionBar, useGameShellFromContext, } from '@labrain/game-shell';
import { createWordPyramidsAdapter, makeStimulus, } from './adapter';
import { Demo } from './demo';
import { WordPyramidsDebriefView, WordPyramidsStaircaseView, } from './staircaseView';
import { paradigmMeta, staircaseConfig, variantForLevel } from './params';
import { LevelSelector } from '../../components/LevelSelector';
import { PedagogieDrawer } from '../../components/PedagogieDrawer';
import { loadProgression, promoteOnSuccess, setCurrentLevel, } from '../../lib/game-progression';
import { loadUserState } from '../../lib/user-state';
const GAME_SLUG = 'word-pyramids';
function Intro({ level, progression, onSelectLevel, onShowDemo, onSkipDemo, onWhy, }) {
    const variant = variantForLevel(level);
    useActionBar({
        primary: (_jsx("button", { type: "button", onClick: onShowDemo, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: "Voir un exemple" })),
        secondary: (_jsx("button", { type: "button", onClick: onSkipDemo, className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: "D\u00E9marrer direct" })),
    });
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-5 py-6 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: paradigmMeta.paradigm }), _jsx("h1", { className: "font-display text-[28px] font-medium leading-9 tracking-cy-display text-cyan-text", children: "Pyramides" }), _jsxs("p", { className: "max-w-[320px] text-[14.5px] leading-5 text-cyan-text-secondary", children: ["Compose le maximum de mots valides \u00E0 partir des lettres en", ' ', Math.round(variant.roundMs / 1000), " secondes."] }), _jsxs("div", { className: "grid w-full grid-cols-2 gap-3 border border-cyan-border-faint bg-cyan-surface p-4 text-start text-[12px] text-cyan-text-secondary", children: [_jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "Niveau" }), _jsxs("span", { className: "text-[14px] text-cyan-text", children: [level, " ", variant.label] })] }), _jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "Round" }), _jsxs("span", { className: "text-[14px] text-cyan-text", children: [Math.round(variant.roundMs / 1000), " s"] })] }), _jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "Bilingue" }), _jsx("span", { className: "text-[14px] text-cyan-text", children: variant.bilingual ? 'Oui (FR/EN)' : 'Non' })] }), _jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: "Rounds" }), _jsxs("span", { className: "text-[14px] text-cyan-text", children: ["jusqu'\u00E0 ", staircaseConfig.maxTrials] })] })] }), _jsx(LevelSelector, { gameSlug: GAME_SLUG, bestLevel: progression.bestLevel, currentLevel: progression.currentLevel, onSelect: onSelectLevel }), _jsx("button", { type: "button", onClick: onWhy, className: "text-[12px] text-cyan-network underline-offset-4 hover:underline", children: "Pourquoi ce jeu ?" }), _jsx(Link, { to: "/library", className: "text-center text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: "Retour aux jeux" })] }));
}
function IntroSlot({ level, progression, onSelectLevel }) {
    const shell = useGameShellFromContext();
    const [pedagogieOpen, setPedagogieOpen] = useState(false);
    return (_jsxs(_Fragment, { children: [_jsx(Intro, { level: level, progression: progression, onSelectLevel: onSelectLevel, onShowDemo: () => shell.startSession(), onSkipDemo: () => shell.skipDemo(), onWhy: () => setPedagogieOpen(true) }), _jsx(PedagogieDrawer, { gameSlug: GAME_SLUG, isOpen: pedagogieOpen, onClose: () => setPedagogieOpen(false) })] }));
}
function DemoSlot() {
    const shell = useGameShellFromContext();
    return (_jsx(Demo, { onComplete: () => shell.markDemoDone(), onSkip: () => shell.markDemoDone() }));
}
function WrapUpSlot({ onCompleted }) {
    const shell = useGameShellFromContext();
    useMemo(() => {
        const threshold = shell.snapshot.staircase.thresholdEstimate;
        try {
            window.localStorage.setItem(`labrain.${GAME_SLUG}.lastRun`, JSON.stringify({
                ts: Date.now(),
                threshold,
            }));
        }
        catch {
            /* ignored */
        }
        onCompleted(threshold);
        shell.markWrapUpDone();
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
function deriveLang(i18nLang) {
    if (!i18nLang)
        return 'fr';
    return i18nLang.startsWith('en') ? 'en' : 'fr';
}
export function WordPyramidsGame() {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lang = deriveLang(i18n.resolvedLanguage ?? i18n.language);
    const [progression, setProgression] = useState(() => loadProgression(GAME_SLUG));
    const level = progression.currentLevel;
    const levelRef = useRef(level);
    levelRef.current = level;
    const langRef = useRef(lang);
    langRef.current = lang;
    const adapterRef = useRef(null);
    if (adapterRef.current === null) {
        adapterRef.current = createWordPyramidsAdapter(level, lang);
    }
    const adapter = adapterRef.current;
    useEffect(() => {
        adapter.setLevel(level);
    }, [adapter, level]);
    const handleSelectLevel = (newLevel) => {
        setProgression(setCurrentLevel(GAME_SLUG, newLevel));
    };
    const handleCompleted = (threshold) => {
        if (threshold !== null && Number.isFinite(threshold)) {
            setProgression(promoteOnSuccess(GAME_SLUG, levelRef.current, threshold));
        }
    };
    const userSettings = useMemo(() => loadUserState().settings, []);
    const config = useMemo(() => ({
        gameSlug: GAME_SLUG,
        gameMaster: 'foudre',
        staircaseConfig,
        showDemo: true,
        showCalibrationWarmup: false,
        enableHaptics: userSettings.enableHaptics,
        enableSound: userSettings.enableSound,
        enableVisualFeedback: true,
    }), [userSettings.enableHaptics, userSettings.enableSound]);
    return (_jsxs(ActionBarProvider, { children: [_jsx(GameShell, { config: config, adapter: adapter, buildStimulus: (engStim) => makeStimulus(engStim.trialIdx, engStim.value, levelRef.current, langRef.current), grader: (_stim, resp) => ({
                    correct: resp.correct,
                    reactionTimeMs: resp.reactionTimeMs,
                    rawInput: { acceptedWords: resp.acceptedWords, score: resp.score },
                }), children: _jsx(PhaseRenderer, { intro: _jsx(IntroSlot, { level: level, progression: progression, onSelectLevel: handleSelectLevel }), demo: _jsx(DemoSlot, {}), staircase: _jsx(WordPyramidsStaircaseView, { adapter: adapter, level: level, onAbort: () => navigate('/library') }), wrapUp: _jsx(WrapUpSlot, { onCompleted: handleCompleted }), debrief: _jsx(WordPyramidsDebriefView, { level: level, onAbort: () => navigate('/library') }), aborted: null }) }), _jsx(ActionBarSlot, {})] }));
}
export default WordPyramidsGame;
//# sourceMappingURL=game.js.map